import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Alert,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Collapse,
    Badge,
    Button,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Security as SecurityIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    FilterList as FilterIcon,
} from '@mui/icons-material';
import AttackDetectionService from '../services/attackDetectionService';
import IPBlockingService from '../services/ipBlockingService';
import FirebaseSecurityService from '../services/firebaseSecurityService';

interface SecurityEvent {
    id: string;
    timestamp: Date;
    severity: 'critical' | 'high' | 'medium' | 'low';
    message: string;
    type: string;
    ip?: string;
    details?: string;
}

const SecurityMonitor: React.FC = () => {
    const [expanded, setExpanded] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // State management for security monitoring
    const [securityStatus, setSecurityStatus] = useState<any>(null);
    const [metrics, setMetrics] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [events, setEvents] = useState<SecurityEvent[]>([]);

    // Load security data from services
    const loadSecurityData = useCallback(async () => {
        try {
            // Initialize Firebase service
            await FirebaseSecurityService.initialize();
            
            // Get data from Firebase (via cache)
            const firebaseEvents = FirebaseSecurityService.getSecurityEvents();
            const blockedIPs = await IPBlockingService.getBlockedIPs();
            const suspiciousActivities = await IPBlockingService.getSuspiciousActivities();
            
            // Also get in-memory attacks (for real-time)
            const attacks = AttackDetectionService.getDetectedAttacks();
            const attackStats = AttackDetectionService.getAttackStatistics();

            // Combine Firebase events with in-memory attacks
            const securityEvents: SecurityEvent[] = [
                // Firebase events (persistent)
                ...firebaseEvents.map(event => ({
                    id: event.id,
                    timestamp: event.timestamp,
                    severity: event.severity,
                    message: event.message,
                    type: event.type,
                    ip: event.ip,
                    details: event.details
                })),
                // In-memory attacks (real-time, not yet in Firebase)
                ...attacks.filter(attack => 
                    !firebaseEvents.some(e => e.ip === attack.ip && e.type === attack.type)
                ).map(attack => ({
                    id: attack.id,
                    timestamp: attack.timestamp,
                    severity: attack.severity,
                    message: `${attack.type.replace(/_/g, ' ')} detected from ${attack.ip}`,
                    type: attack.type,
                    ip: attack.ip,
                    details: attack.details.endpoint || attack.details.payload || ''
                }))
            ];

            // Add blocked IP events
            blockedIPs.forEach(blocked => {
                // Determine severity based on reason
                let severity: 'critical' | 'high' | 'medium' | 'low' = 'high';
                const reason = blocked.reason.toLowerCase();
                if (reason.includes('ddos') || reason.includes('critical')) {
                    severity = 'critical';
                } else if (reason.includes('brute force') || reason.includes('sql injection')) {
                    severity = 'high';
                } else if (reason.includes('suspicious')) {
                    severity = 'medium';
                }
                
                securityEvents.push({
                    id: blocked.id,
                    timestamp: blocked.blockedAt,
                    severity,
                    message: `IP ${blocked.ip} blocked: ${blocked.reason}`,
                    type: 'ip_blocked',
                    ip: blocked.ip,
                    details: blocked.reason
                });
            });

            // Sort by timestamp (newest first)
            securityEvents.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

            setEvents(securityEvents);

            // Calculate security status
            const criticalCount = securityEvents.filter(e => e.severity === 'critical').length;
            const highCount = securityEvents.filter(e => e.severity === 'high').length;
            const totalEvents = securityEvents.length;

            const status = criticalCount > 0 ? 'critical' : 
                          highCount > 5 ? 'warning' : 
                          'healthy';

            setSecurityStatus({
                status,
                lastChecked: new Date().toISOString(),
                totalEvents,
                criticalEvents: criticalCount,
                highEvents: highCount,
                mediumEvents: securityEvents.filter(e => e.severity === 'medium').length,
                lowEvents: securityEvents.filter(e => e.severity === 'low').length
            });

            // Set metrics
            const uniqueIPs = new Set(securityEvents.map(e => e.ip).filter(Boolean)).size;
            setMetrics({
                uniqueIPs,
                blockedIPs: blockedIPs.length,
                suspiciousActivities: suspiciousActivities.length,
                totalAttacks: attackStats.total,
                autoBlockedIPs: attackStats.autoBlocked,
                uniqueUsers: 0 // Placeholder
            });

            setError(null);
        } catch (err) {
            console.error('Failed to load security data:', err);
            setError('Failed to load security data');
        }
    }, []);

    // Refresh function
    const refresh = useCallback(async () => {
        setIsRefreshing(true);
        await loadSecurityData();
        setIsRefreshing(false);
    }, [loadSecurityData]);

    // Load data on mount and set up auto-refresh
    useEffect(() => {
        loadSecurityData().then(() => setIsLoading(false));
        const interval = setInterval(loadSecurityData, 10000); // Refresh every 10 seconds
        return () => clearInterval(interval);
    }, [loadSecurityData]);

    // Simple helper functions
    const getEventsBySeverity = useCallback((severity: string) => {
        return events.filter(event => event.severity === severity);
    }, [events]);

    const getRecentEvents = useCallback((limit: number = 10) => {
        return events.slice(0, limit);
    }, [events]);

    const exportEvents = useCallback((format: 'json' | 'csv') => {
        // Simple export implementation
        const data = format === 'json'
            ? JSON.stringify(events, null, 2)
            : events.map(event => `${event.timestamp || ''},${event.severity || ''},${event.message || ''}`).join('\n');
        const blob = new Blob([data], { type: format === 'json' ? 'application/json' : 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `security-events.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [events]);

    const isSystemHealthy = useCallback(() => {
        return securityStatus?.status === 'healthy';
    }, [securityStatus]);

    // Calculate security score based on events
    const calculateSecurityScore = useCallback(() => {
        if (!events.length) return 100;
        
        let score = 100;
        const criticalCount = events.filter(e => e.severity === 'critical').length;
        const highCount = events.filter(e => e.severity === 'high').length;
        const mediumCount = events.filter(e => e.severity === 'medium').length;
        
        // Deduct points based on severity
        score -= criticalCount * 10;
        score -= highCount * 5;
        score -= mediumCount * 2;
        
        return Math.max(0, Math.min(100, score));
    }, [events]);

    const securityScore = calculateSecurityScore();

    // Generate health status with issues and recommendations
    const healthStatus = useCallback(() => {
        const issues: string[] = [];
        const recommendations: string[] = [];
        
        const criticalEvents = events.filter(e => e.severity === 'critical');
        const highEvents = events.filter(e => e.severity === 'high');
        
        if (criticalEvents.length > 0) {
            issues.push(`${criticalEvents.length} critical security events detected`);
            recommendations.push('Immediately investigate and block malicious IPs');
        }
        
        if (highEvents.length > 5) {
            issues.push(`${highEvents.length} high-severity events require attention`);
            recommendations.push('Review security logs and update firewall rules');
        }
        
        if (metrics?.blockedIPs > 10) {
            issues.push(`${metrics.blockedIPs} IPs currently blocked`);
            recommendations.push('Review blocked IPs list and consider permanent bans');
        }
        
        const status = criticalEvents.length > 0 ? 'critical' : 
                      highEvents.length > 5 ? 'warning' : 
                      'healthy';
        
        return { status, issues, recommendations };
    }, [events, metrics])();

    // Get events by severity
    const criticalEvents = getEventsBySeverity('critical');
    const highEvents = getEventsBySeverity('high');
    const mediumEvents = getEventsBySeverity('medium');
    const lowEvents = getEventsBySeverity('low');

    // Get recent events for display
    const recentEvents = getRecentEvents(5);

    // Handle refresh
    const handleRefresh = async () => {
        await refresh();
    };

    // Handle export
    const handleExport = (format: 'csv' | 'json') => {
        exportEvents(format);
    };

    // Get severity color and icon
    const getSeverityColor = (severity: string) => {
        const colors = {
            'critical': '#d32f2f',
            'high': '#f57c00',
            'medium': '#fbc02d',
            'low': '#388e3c'
        };
        return colors[severity as keyof typeof colors] || '#666';
    };

    const getSeverityIcon = (severity: string) => {
        switch (severity) {
            case 'critical': return <ErrorIcon color="error" />;
            case 'high': return <WarningIcon color="warning" />;
            case 'medium': return <WarningIcon color="info" />;
            case 'low': return <CheckCircleIcon color="success" />;
            default: return <CheckCircleIcon />;
        }
    };

    // Show loading state
    if (isLoading) {
        return (
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography>Loading security data...</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon color="primary" />
                        <Typography variant="h6" component="h2">
                            Security Monitor
                        </Typography>
                        <Badge badgeContent={criticalEvents.length} color="error">
                            <WarningIcon color="error" />
                        </Badge>
                        {!isSystemHealthy && (
                            <Chip label="System Issues" color="error" size="small" />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={handleRefresh} disabled={isRefreshing}>
                                {isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Export Data">
                            <IconButton onClick={() => setShowFilters(!showFilters)}>
                                <FilterIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            onClick={() => setExpanded(!expanded)}
                            sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                </Box>

                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Security Status Summary */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                        label={`${criticalEvents.length} Critical`}
                        color="error"
                        size="small"
                        icon={<ErrorIcon />}
                    />
                    <Chip
                        label={`${highEvents.length} High`}
                        color="warning"
                        size="small"
                        icon={<WarningIcon />}
                    />
                    <Chip
                        label={`${mediumEvents.length} Medium`}
                        color="info"
                        size="small"
                        icon={<WarningIcon />}
                    />
                    <Chip
                        label={`${lowEvents.length} Low`}
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon />}
                    />
                    {metrics && (
                        <>
                            <Chip
                                label={`${metrics.uniqueIPs} Unique IPs`}
                                color="secondary"
                                size="small"
                            />
                            <Chip
                                label={`${metrics.uniqueUsers} Users`}
                                color="default"
                                size="small"
                            />
                        </>
                    )}
                    {securityScore !== undefined && (
                        <Chip
                            label={`Security Score: ${securityScore}/100`}
                            color={securityScore >= 80 ? 'success' : securityScore >= 60 ? 'warning' : 'error'}
                            size="small"
                        />
                    )}
                </Box>

                {/* Export and Filter Controls */}
                {showFilters && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Export & Filter Options
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleExport('csv')}
                            >
                                Export CSV
                            </Button>
                            <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleExport('json')}
                            >
                                Export JSON
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Critical Events Alert */}
                {criticalEvents.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">
                            🚨 {criticalEvents.length} Critical Security Event{criticalEvents.length > 1 ? 's' : ''} Detected
                        </Typography>
                    </Alert>
                )}

                {/* System Health Issues */}
                {!isSystemHealthy && healthStatus.issues.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">
                            System Health Issues Detected
                        </Typography>
                        <ul>
                            {healthStatus.issues.map((issue, index) => (
                                <li key={index}>{issue}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {/* Expanded Details */}
                <Collapse in={expanded}>
                    <Box sx={{ mt: 2 }}>

                        {/* Security Metrics */}
                        {metrics && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                    Security Metrics
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="h6" color="primary">
                                            {metrics.totalEvents}
                                        </Typography>
                                        <Typography variant="caption">Total Events</Typography>
                                    </Box>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="h6" color="error">
                                            {metrics.criticalEvents}
                                        </Typography>
                                        <Typography variant="caption">Critical Events</Typography>
                                    </Box>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="h6" color="warning">
                                            {metrics.highEvents}
                                        </Typography>
                                        <Typography variant="caption">High Severity</Typography>
                                    </Box>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="h6" color="info">
                                            {metrics.eventsPerHour}
                                        </Typography>
                                        <Typography variant="caption">Events/Hour</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Recent Security Events */}
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                            Recent Security Events
                        </Typography>
                        <List dense>
                            {recentEvents.map((event, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getSeverityIcon(event.severity)}
                                                <Typography variant="body2" component="span">
                                                    {event.message}
                                                </Typography>
                                                <Chip
                                                    label={event.severity}
                                                    color={getSeverityColor(event.severity) as any}
                                                    size="small"
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(event.timestamp).toLocaleString()} - {event.ip || 'Unknown IP'}
                                                {event.details && ` - ${event.details.substring(0, 50)}`}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>

                        {/* Security Status Details */}
                        {securityStatus && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                    System Status
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Server Status</Typography>
                                        <Typography variant="h6" color={securityStatus.server === 'online' ? 'success.main' : 'error.main'}>
                                            {securityStatus.server}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Database</Typography>
                                        <Typography variant="h6" color={securityStatus.database === 'connected' ? 'success.main' : 'error.main'}>
                                            {securityStatus.database}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Security</Typography>
                                        <Typography variant="h6" color={securityStatus.security === 'active' ? 'success.main' : 'error.main'}>
                                            {securityStatus.security}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Uptime</Typography>
                                        <Typography variant="h6">
                                            {Math.floor(securityStatus.uptime / 3600)}h
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Metrics Summary */}
                        {metrics && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                    Security Metrics
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                    <Chip
                                        label={`Total Attacks: ${metrics.totalAttacks || 0}`}
                                        color="error"
                                        size="small"
                                    />
                                    <Chip
                                        label={`Blocked IPs: ${metrics.blockedIPs || 0}`}
                                        color="warning"
                                        size="small"
                                    />
                                    <Chip
                                        label={`Auto-Blocked: ${metrics.autoBlockedIPs || 0}`}
                                        color="info"
                                        size="small"
                                    />
                                    <Chip
                                        label={`Unique IPs: ${metrics.uniqueIPs || 0}`}
                                        color="default"
                                        size="small"
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Security Recommendations */}
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Security Recommendations
                            </Typography>
                            <List dense>
                                {healthStatus.recommendations.length > 0 ? (
                                    healthStatus.recommendations.map((recommendation, index) => (
                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                            <ListItemText
                                                primary={`💡 ${recommendation}`}
                                                secondary="System recommendation"
                                            />
                                        </ListItem>
                                    ))
                                ) : (
                                    <>
                                        <ListItem sx={{ py: 0.5 }}>
                                            <ListItemText
                                                primary="✅ Input validation enabled"
                                                secondary="All user inputs are validated and sanitized"
                                            />
                                        </ListItem>
                                        <ListItem sx={{ py: 0.5 }}>
                                            <ListItemText
                                                primary="✅ Rate limiting active"
                                                secondary="Prevents abuse and DDoS attacks"
                                            />
                                        </ListItem>
                                        <ListItem sx={{ py: 0.5 }}>
                                            <ListItemText
                                                primary="✅ XSS protection enabled"
                                                secondary="Content Security Policy and XSS filters active"
                                            />
                                        </ListItem>
                                        <ListItem sx={{ py: 0.5 }}>
                                            <ListItemText
                                                primary="✅ CORS protection"
                                                secondary="Cross-origin requests are restricted"
                                            />
                                        </ListItem>
                                        <ListItem sx={{ py: 0.5 }}>
                                            <ListItemText
                                                primary="✅ Bot detection"
                                                secondary="Suspicious user agents are blocked"
                                            />
                                        </ListItem>
                                    </>
                                )}
                            </List>
                        </Box>
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default SecurityMonitor; 